interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface Window {
    __earlyPWAPrompt: BeforeInstallPromptEvent | null;
  }
}

// =====================================================
// EARLY PWA PROMPT CAPTURE - Must run before React mounts
// =====================================================
// Store the beforeinstallprompt event immediately when it fires
// This prevents losing the event if it fires before React components mount
if (typeof window !== 'undefined') {
  window.__earlyPWAPrompt = null;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    // o lib.dom não tipa beforeinstallprompt; o evento que chega aqui é o BeforeInstallPromptEvent
    window.__earlyPWAPrompt = e as BeforeInstallPromptEvent;
    logger.log('[PWA] 🎯 Early capture: beforeinstallprompt event stored globally');
  });
}

// Antes do React: o tradutor do navegador não pode derrubar a tela (ver o arquivo).
import "./lib/tradutorSeguro";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { registerSW } from 'virtual:pwa-register';
import { logger } from '@/lib/logger';
import { IS_FR } from '@/lib/i18n';

if (IS_FR) {
  document.documentElement.lang = 'fr';
  document.title = 'La Méthode des 3 Phrases';
  document.querySelector('meta[name="description"]')?.setAttribute('content', 'Des phrases concrètes pour les moments difficiles avec votre enfant, avec Sarah, votre assistante IA.');
}

// =====================================================
// PWA SERVICE WORKER REGISTRATION (P-11, 2026-04-18)
// =====================================================
// registerType: "autoUpdate" in vite.config forces Workbox skipWaiting + clientsClaim.
// The new SW activates automatically in the background on every deploy.
// usePWAUpdate hook (see src/hooks/usePWAUpdate.ts) handles the in-app reload
// on the next React Router navigation so users see the new version instantly
// without a modal/banner.
if ('serviceWorker' in navigator) {
  registerSW({
    immediate: true,
    onNeedRefresh() {
      // Under autoUpdate this dispatches ONLY if the skipWaiting flow is blocked.
      // Our hook listens for it as a safety net. In the common case, Workbox
      // swaps the SW silently and the hook reloads on next route change.
      logger.log('🔄 Service Worker update available');
      window.dispatchEvent(new CustomEvent('sw-update-available'));
    },
    onOfflineReady() {
      logger.log('✅ App ready to work offline');
    },
    onRegisteredSW(swScriptUrl, registration) {
      logger.log('✅ Service Worker registered:', swScriptUrl);

      // Poll for updates every 30 min for long-running tabs that never navigate.
      // Workbox compares the new sw.js hash; no-op if identical.
      if (registration) {
        setInterval(() => {
          registration.update().catch(() => {/* swallow — next tick retries */});
        }, 30 * 60 * 1000);
      }
    },
    onRegisterError(error) {
      logger.error('❌ Service Worker registration error:', error);
    },
  });
} else {
  logger.warn('⚠️ Service Worker not supported in this browser');
}

// =====================================================
// PEDAÇO DESATUALIZADO RECARREGA A PÁGINA (20/09/2026, medido em produção)
// =====================================================
// Ele reportou "a página Learn tá bugada, não entra" num dia com cinco deploys.
// O rastro: `autoUpdate` faz o service worker novo assumir a página aberta em
// silêncio e apagar os pedaços do deploy anterior; a página em uso continua com
// o index.html antigo em memória; ao tocar numa rota lazy ela pede um
// `Learn-<hash-velho>.js` que já não existe; a Vercel responde ao caminho
// inexistente com o index.html, 200, text/html; o navegador recusa HTML como
// módulo e a rota fica branca. O refresh resolvia porque pegava o index novo.
// No PWA instalado não há refresh, então ficava assim até fechar o app.
//
// O `usePWAUpdate` não cobre: ele só age em `sw-update-available`, que sob
// autoUpdate quase nunca dispara. Este é o evento certo: o Vite emite
// `vite:preloadError` exatamente quando um import() dinâmico falha. Recarregar
// aqui é a recomendação oficial para deploys com hash no nome.
//
// Guarda contra laço: se recarregou há menos de 30 s e falhou de novo, o
// problema é outro (deploy quebrado, rede), e insistir viraria reload infinito.
window.addEventListener('vite:preloadError', (event) => {
  const CHAVE = 'coo:recarregou-por-pedaco-velho';
  let ultimo = 0;
  try { ultimo = Number(sessionStorage.getItem(CHAVE) || 0); } catch { /* modo privado */ }
  if (Date.now() - ultimo < 30_000) {
    logger.error('[preloadError] já recarregou há menos de 30 s; não vai insistir', event);
    return;
  }
  event.preventDefault();
  try { sessionStorage.setItem(CHAVE, String(Date.now())); } catch { /* modo privado */ }
  logger.warn('[preloadError] pedaço desatualizado depois de um deploy; recarregando');
  window.location.reload();
});

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);

// Mede a velocidade real no aparelho da usuária e grava uma linha por sessão.
// Depois do render de propósito: a medição não pode disputar a primeira tela
// com o app. Carregado à parte para não entrar no pacote de entrada.
void import('@/lib/webVitals').then((m) => m.initWebVitals());
